import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { practiceAPI } from '../../api';
import ImportWizard from './ImportWizard';
import { Sidebar } from './dashboard_component';

const PrepArenaDashboard = () => {
  const navigate = useNavigate();
  const [sets, setSets] = useState([]);
  const [statsMap, setStatsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showImportModal, setShowImportModal] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const setsRes = await practiceAPI.getMySets();
      const userSets = Array.isArray(setsRes.data) ? setsRes.data : [];
      setSets(userSets);

      const statsPromises = userSets.map(set =>
        practiceAPI.getStats(set.set_id).catch(() => ({ data: null }))
      );
      const statsResults = await Promise.all(statsPromises);
      const statsObj = {};
      statsResults.forEach((res, idx) => {
        if (res?.data) statsObj[userSets[idx].set_id] = res.data;
      });
      setStatsMap(statsObj);
    } catch (error) {
      console.error(error);
      toast.error('Failed to load practice sets');
      setSets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const startPractice = (setId) => {
    navigate(`/participate/preparena/practice/${setId}`);
  };

  const deleteSet = async (setId, setTitle) => {
    if (!window.confirm(`Delete "${setTitle}" and all its practice data? This cannot be undone.`)) {
      return;
    }
    try {
      await practiceAPI.deleteSet(setId);
      toast.success(`"${setTitle}" deleted successfully`);
      fetchData(); // refresh the list
    } catch (error) {
      console.error(error);
      toast.error('Failed to delete practice set');
    }
  };

  const handleImportSuccess = () => {
    setShowImportModal(false);
    fetchData();
    toast.success('Questions imported successfully!');
  };

  const totalQuestions = sets.reduce((sum, set) => sum + (set.total_questions || 0), 0);
  const avgScore = Object.values(statsMap).reduce((acc, s) => acc + (s.average_score || 0), 0);
  const bestAvg = Object.keys(statsMap).length ? Math.round(avgScore / Object.keys(statsMap).length) : 0;

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      <Sidebar isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
      <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">PrepArena</h2>
                  <p className="text-blue-100">Practice your own questions – reinforce your memory</p>
                </div>
                <button
                  onClick={() => setShowImportModal(true)}
                  className="mt-4 md:mt-0 bg-white text-blue-600 hover:bg-blue-50 font-semibold py-3 px-6 rounded-xl shadow-md transition-all flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Import Questions
                </button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Practice Sets</p>
                  <p className="text-2xl font-bold text-gray-900">{sets.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Questions</p>
                  <p className="text-2xl font-bold text-gray-900">{totalQuestions}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Best Average</p>
                  <p className="text-2xl font-bold text-gray-900">{bestAvg}%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Practice Sets List */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-green-600 to-emerald-700 px-6 py-4">
              <h2 className="text-xl font-bold text-white">My Practice Sets</h2>
            </div>
            {loading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>
            ) : sets.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No practice sets yet</h3>
                <button onClick={() => setShowImportModal(true)} className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg">Import your first questions</button>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {sets.map(set => {
                  const stats = statsMap[set.set_id] || {};
                  return (
                    <div key={set.set_id} className="p-6 hover:bg-gray-50 transition">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">{set.title}</h3>
                          <p className="text-sm text-gray-500 mt-1">{set.total_questions} questions</p>
                          <div className="flex flex-wrap gap-4 mt-2 text-sm">
                            <span className="text-gray-600">Attempts: {stats.total_attempts || 0}</span>
                            <span className="text-green-600">Best: {Math.round(stats.best_score || 0)}%</span>
                            <span className="text-blue-600">Avg: {Math.round(stats.average_score || 0)}%</span>
                          </div>
                        </div>
                        <div className="mt-4 md:mt-0 flex gap-3">
                          <button
                            onClick={() => startPractice(set.set_id)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition"
                          >
                            Practice
                          </button>
                          <button
                            onClick={() => deleteSet(set.set_id, set.title)}
                            className="bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-lg font-medium transition"
                            title="Delete set"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <ImportWizard isOpen={showImportModal} onClose={() => setShowImportModal(false)} onSuccess={handleImportSuccess} />
    </div>
  );
};

export default PrepArenaDashboard;